import { Strategy, TacticalAi } from './ai.js';

const AI_DEBUG = false;

/**
 * Heuristic (rule-based) strategic planner. Extends TacticalAi by implementing
 * runStrategicPlan() with the classic spawner-priority heuristics.
 */
class HeuristicAi extends TacticalAi {

    /**
     * @param {number} faction
     * @param {import('./util.js').EntitySystem} entitySystem
     * @param {import('./util.js').GridSystem|null} [locationGrid]
     */
    constructor(faction, entitySystem, locationGrid = null) {
        super(faction, entitySystem, locationGrid);
    }

    runStrategicPlan() {
        this.determineStrategy();
        this.specifyTargets();
    }

    determineStrategy() {
        var s, balanceChange, spawner, spawnerBalance = 0, globalMyUnitCount,
            enemySpawnerDepth = 0;

        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            spawner = this.spawnerAnalysis[s].object;

            balanceChange = 0;
            if (spawner.capturedByFaction === this.faction) {
                balanceChange = 1;
            } else if (spawner.capturedByFaction !== 0) {
                balanceChange = -1;
            }
            if (!spawner.isFullyCaptured()) {
                balanceChange *= 0.5;
            }
            spawnerBalance += balanceChange;

            if (spawner.capturedByFaction !== 0 && spawner.capturedByFaction !== this.faction) {
                enemySpawnerDepth += 1;
            }
            // twm: possible bug here due to this only being added to in order
            this.spawnerAnalysis[s].enemyDepth = enemySpawnerDepth;
            this.spawnerAnalysis[s].unitCounts = this.getUnitCount(this.spawnerAnalysis[s].sectors);
        }

        globalMyUnitCount = this.getGlobalStrength(this.faction);
        const totalStrength = this.entitySystem.units.reduce((sum, u) => sum + TacticalAi.unitStrength(u), 0);
        this.unitBalance = totalStrength > 0 ? globalMyUnitCount / Math.max(1, totalStrength - globalMyUnitCount) : 1;

        if ((spawnerBalance <= -1 && this.unitBalance < 0.9) || this.unitBalance < 0.75) {
            // twm: Possible issue here that it will never attack if it has fewer spawners, no matter how many units
            this.strategy = Strategy.Defend;
        } else if (spawnerBalance >= 0 && this.unitBalance > 1.1) {
            this.strategy = Strategy.Attack;
        } else {
            this.strategy = Strategy.Maintain;
        }

        if (AI_DEBUG) {
            console.log("AI", this.faction, "determines best strategy is", this.strategy,
                "(10=D,20=M,30=A) - spawnerBalance", spawnerBalance, "unitBalance",
                this.unitBalance);
        }
    }

    specifyTargets() {
        var s,
            globalMyUnitCount = this.getGlobalStrength(this.faction);

        this.prioritySum = 0;

        if (this.strategy === Strategy.Defend) {
            this.specifyTargetsDefend();
        } else if (this.strategy === Strategy.Attack) {
            this.specifyTargetsAttack();
        } else {
            this.specifyTargetsMaintain();
        }

        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            this.spawnerAnalysis[s].unitsToSend =
                globalMyUnitCount * this.spawnerAnalysis[s].priority / this.prioritySum;

            if (AI_DEBUG) {
                console.log("AI", this.faction, "priority for spawner", s,
                    "is", this.spawnerAnalysis[s].priority,
                    "- unitsToSend", this.spawnerAnalysis[s].unitsToSend,
                    "- prioritySum", this.prioritySum);
            }
        }
    }

    // DEFEND at all costs!
    specifyTargetsDefend() {
        var s, sa, spawner;

        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            sa = this.spawnerAnalysis[s];
            spawner = sa.object;

            sa.priority = 0;

            // defend attacked flag with almost full force
            if (spawner.capturedByFaction === this.faction &&
                sa.unitCounts[1] > 0 && sa.unitCounts[0] / sa.unitCounts[1] < 2) {
                sa.priority = 40;

                // defend based on distance from HQ
            } else if (spawner.capturedByFaction === this.faction) {
                sa.priority = s + 1;

            }

            this.prioritySum += sa.priority;
        }
    }

    // MAINTAIN current status!
    specifyTargetsMaintain() {
        var s, sa, fullyCaptured, isNeutral, spawner, priority,
            neutralBeingAttacked = 0, saED1 = null, saED2 = null,
            globalMyUnitCount = this.getGlobalStrength(this.faction);

        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            sa = this.spawnerAnalysis[s];
            spawner = sa.object;
            fullyCaptured = spawner.isFullyCaptured();
            isNeutral = !spawner.isFullyCaptured() &&
                (spawner.capture.capturePointStatus / spawner.capture.capturePoints) < 0.333;

            sa.priority = 0;

            if (neutralBeingAttacked < this.maxDesignations) {
                if (isNeutral && spawner.capturedByFaction === 0) {
                    // Go to spawners that are less than 33% claimed
                    sa.priority = 10;
                    neutralBeingAttacked += 1;
                } else if (!fullyCaptured && spawner.capturedByFaction === this.faction) {
                    // Reinforce ones we're capturing
                    sa.priority = 10;
                    neutralBeingAttacked += 1;
                } else if (isNeutral) {
                    // Grab unclaimed spawners
                    sa.priority = 10;
                    neutralBeingAttacked += 1;
                }
            }

            // defend flag that is being attacked
            if (spawner.capturedByFaction === this.faction &&
                sa.unitCounts[1] > 0 && sa.unitCounts[0] / sa.unitCounts[1] < 2) {
                sa.priority = 30;

                // put enough units to flag to be flexible
            } else if (spawner.capturedByFaction === this.faction) {
                sa.priority = Math.max(sa.priority, s + 1);

            }

            if (sa.enemyDepth === 1) {
                saED1 = sa;
            } else if (sa.enemyDepth === 2) {
                saED2 = sa;
            }

            this.prioritySum += sa.priority;
        }

        // if nothing has to be captured, be a little bit annoying to opponent
        if (neutralBeingAttacked === 0 && this.unitBalance >= 0.99 && saED1 !== null) {
            priority = this.prioritySum / 3;

            // see which target is more attractive
            if (saED2 !== null && saED2.unitCounts[1] < saED1.unitCounts[1] / 1.5) {
                sa = saED2;
            } else {
                sa = saED1;
            }

            // only attack if there is the slightest chance
            if (sa.unitCounts[1] < globalMyUnitCount / 2) {
                sa.priority = priority;
                this.prioritySum += priority;
            }
        }
    }

    // ATTACK!
    specifyTargetsAttack() {
        var s, sa, fullyCaptured, spawner, isNeutral,
            globalMyUnitCount = this.getGlobalStrength(this.faction),
            maxEnemySpawnerDepth = Math.max(1000, this.maxDesignations),
            designations = 0;

        for (s = 0; s < this.spawnerAnalysis.length; s += 1) {
            sa = this.spawnerAnalysis[s];
            spawner = sa.object;
            fullyCaptured = spawner.isFullyCaptured();
            isNeutral = !spawner.isFullyCaptured() &&
                (spawner.capture.capturePointStatus / spawner.capture.capturePoints) < 0.333;

            if (spawner.capturedByFaction === this.faction &&
                sa.unitCounts[1] > 0 && sa.unitCounts[0] / sa.unitCounts[1] < 2) {
                // twm I think this is not being triggered often enough
                // defend flag that is being attacked
                sa.priority = this.spawnerAnalysis.length * 5;
                designations += 1;

            } else if (designations < this.maxDesignations &&
                sa.enemyDepth > 0 && sa.enemyDepth <= maxEnemySpawnerDepth &&
                sa.unitCounts[1] < globalMyUnitCount / 1.5) {
                // Reinforce
                sa.priority = this.spawnerAnalysis.length * 4;
                designations += 1;

            } else if (!fullyCaptured && spawner.capturedByFaction === this.faction) {
                // reinforce ones that aren't fully captured
                sa.priority = this.spawnerAnalysis.length * 2;
                designations += 1;

            } else if (designations < this.maxDesignations &&
                isNeutral && spawner.capturedByFaction !== this.faction) {
                // Try to grab ones that are close to captured
                sa.priority = this.spawnerAnalysis.length * 3;
                designations += 1;

            } else if (spawner.capturedByFaction === this.faction) {
                sa.priority = s + 1;

            } else {
                sa.priority = 0;

            }

            this.prioritySum += sa.priority;
        }
    }
}

export { HeuristicAi };
